import { Component } from '@theme/component';

/**
 * @typedef {object} Refs
 * @property {HTMLElement} [count] - "12 shirts": `data-one` / `data-other` with __COUNT__.
 * @property {HTMLElement} [empty] - Shown when nothing matches the chosen chip.
 * @property {HTMLElement} [more] - The "Show more" area, hidden once every match is showing.
 * @property {HTMLElement} [shown] - "Showing 24 of 64": `data-template` with __SHOWN__ and __TOTAL__.
 */

/**
 * Filters a server-rendered list by theme chip, with an optional "Show more" (V3 reviews page and
 * inspiration wall, design-handoff/design/Reviews V3.dc.html and Inspiration V3.dc.html).
 *
 * - Items are the `[data-filter-item]` descendants; `data-filter` holds the item's theme handle.
 * - Chips are radios (`<input data-filter-chip value="all|glam|…" on:change="/filter">`, inside a
 *   `.fifi-tag` label); "all" matches every item.
 * - `data-page-size` on the element shows that many matches, plus that many more per
 *   `on:click="/showMore"`. Without it every match shows.
 *
 * Liquid renders the first state (counts, hidden items), so the page reads the same without JS.
 * Strings come from Liquid with brace-free placeholders.
 *
 * @extends {Component<Refs>}
 */
export class FilterGrid extends Component {
  #filter = 'all';

  #limit = Infinity;

  connectedCallback() {
    super.connectedCallback();

    const checked = this.querySelector('input[data-filter-chip]:checked');
    if (checked instanceof HTMLInputElement) this.#filter = checked.value;
    this.#limit = this.#pageSize;
    this.refresh();
  }

  /** @returns {number} */
  get #pageSize() {
    const size = Number(this.dataset.pageSize);
    return size > 0 ? size : Infinity;
  }

  /** @returns {HTMLElement[]} */
  get items() {
    return Array.from(this.querySelectorAll('[data-filter-item]')).filter(
      /** @returns {element is HTMLElement} */
      (element) => element instanceof HTMLElement
    );
  }

  /** The items matching the chosen chip, in page order. @returns {HTMLElement[]} */
  get matchingItems() {
    return this.items.filter((item) => this.#filter === 'all' || item.dataset.filter === this.#filter);
  }

  /** @param {Event} event */
  filter(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;

    this.#filter = input.value;
    this.#limit = this.#pageSize;
    this.refresh();
  }

  showMore() {
    this.#limit += this.#pageSize;
    this.refresh();
  }

  /** Shows the first `limit` matches and updates the count, empty state and "Show more". */
  refresh() {
    const matches = new Set(this.matchingItems);
    let shown = 0;

    for (const item of this.items) {
      const visible = matches.has(item) && shown < this.#limit;
      if (visible) shown += 1;
      item.hidden = !visible;
    }

    const total = matches.size;
    const { count, empty, more, shown: shownLine } = this.refs;

    if (count) {
      const template = (total === 1 ? count.dataset.one : count.dataset.other) ?? '';
      count.textContent = template.replace('__COUNT__', String(total));
    }
    if (empty) empty.hidden = total > 0;
    if (more) more.hidden = shown >= total;
    if (shownLine) {
      shownLine.textContent = (shownLine.dataset.template ?? '')
        .replace('__SHOWN__', String(shown))
        .replace('__TOTAL__', String(total));
    }
  }
}

if (!customElements.get('fifi-filter-grid')) {
  customElements.define('fifi-filter-grid', FilterGrid);
}
